import React, { useEffect, useRef, useState } from 'react';
import { checkForNewVersion, reloadApp } from '../../services/version.service';

/**
 * Component that periodically checks for new versions
 * Shows a notification banner when update is available
 */
const VersionChecker: React.FC = () => {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const isCheckingRef = useRef(false);

  useEffect(() => {
    const checkVersion = async () => {
      if (isCheckingRef.current) return;

      isCheckingRef.current = true;
      try {
        const hasUpdate = await checkForNewVersion();
        if (hasUpdate) {
          setShowUpdateBanner(true);
        }
      } catch (error) {
        console.error('Version check error:', error);
      } finally {
        isCheckingRef.current = false;
      }
    };

    // Initial check after 10 seconds
    const initialTimeout = setTimeout(checkVersion, 10000);

    // Check every 5 minutes
    const interval = setInterval(checkVersion, 5 * 60 * 1000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);

  if (!showUpdateBanner) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: '#2196F3',
        color: 'white',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 10000,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        animation: 'slideDown 0.3s ease-out'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '20px' }}>🔄</span>
        <span style={{ fontWeight: '500' }}>
          A new version is available! Please refresh to get the latest updates.
        </span>
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={reloadApp}
          style={{
            backgroundColor: 'white',
            color: '#2196F3',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 20px',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          Refresh Now
        </button>
        <button
          onClick={() => setShowUpdateBanner(false)}
          style={{
            backgroundColor: 'transparent',
            color: 'white',
            border: '1px solid white',
            borderRadius: '4px',
            padding: '8px 16px',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          Later
        </button>
      </div>
      <style>{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default VersionChecker;
